'use client';

import React, {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';

import { createPortal } from 'react-dom';

import { Toast } from '@depop/web-ui-kit/Toast';

import { type ToastState } from './types';

export const DEFAULT_STATE = {
  setToastState: () => null,
  toastState: {},
};

type State = ToastState | Record<string, never>;

export type ToastContextType = {
  setToastState: Dispatch<SetStateAction<State>>;
  toastState: State;
};

export const ToastContext = createContext<ToastContextType>(DEFAULT_STATE);

/**
 * ToastProvider is a context provider that allows us to display error/success toast messages. Must be used in conjunction with useToast hook.
 * @example
 * <ToastProvider>
 *  <MyComponent />
 * </ToastProvider>
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [isClient, setIsClient] = useState(false);
  const [toastState, setToastState] = useState<State>({});

  function setOpen() {
    setToastState({});
  }

  useEffect(() => {
    setIsClient(true);
  }, []);

  const { classname, description, status, title } = toastState;

  const isOpen = !!status && !!title;

  return (
    <ToastContext.Provider value={{ setToastState, toastState }}>
      {children}
      {/* To prevent hydration mismatch errors, only render the Toast on the client */}
      {isClient &&
        createPortal(
          <Toast
            isOpen={isOpen}
            setOpen={setOpen}
            description={description}
            className={classname}
            status={status}
            title={title}
          />,
          document.body
        )}
    </ToastContext.Provider>
  );
}
